import type { InputProps } from '@chakra-ui/react';
import { Group, Input, InputElement } from '@chakra-ui/react';
import { Search as SearchIcon, X } from 'lucide-react';
import * as React from 'react';

import {
  searchContainerPadPx,
  searchDefaultSize,
  searchGapPx,
  searchIconBoxSizes,
  searchIconSizes,
  type SearchSize,
} from './search.kds';

export interface SearchProps extends Omit<InputProps, 'size' | 'type'> {
  /** KDS size — `sm`, `md`, or `lg`. */
  size?: SearchSize;
  /** Callback fired when the clear button is clicked. */
  onClear?: () => void;
}

export const Search = React.forwardRef<HTMLInputElement, SearchProps>(function Search(props, ref) {
  const { size = searchDefaultSize, value, onClear, width, maxWidth, minWidth, ...inputProps } = props;

  const iconSize = searchIconSizes[size];
  const iconBoxPx = searchIconBoxSizes[size];
  const slotWidth = `${searchContainerPadPx + iconBoxPx}px`;
  const inputPadding = `${searchContainerPadPx + iconBoxPx + searchGapPx}px`;

  const filled = String(value ?? '').length > 0;
  const showClear = filled && !inputProps.disabled && onClear != null;

  return (
    <Group width={width ?? 'full'} maxWidth={maxWidth} minWidth={minWidth}>
      <InputElement pointerEvents="none" px="0" ps="2" width={slotWidth}>
        <SearchIcon size={iconSize} style={{ flexShrink: 0 }} />
      </InputElement>

      <Input
        ref={ref}
        type="text"
        size={size}
        variant="outline"
        placeholder="Search"
        autoComplete="off"
        value={value}
        ps={inputPadding}
        pe={showClear ? inputPadding : undefined}
        _focusVisible={{ borderColor: 'brand.400' }}
        {...inputProps}
      />

      {showClear && (
        <InputElement placement="end" px="0" pe="2" width={slotWidth}>
          <button
            type="button"
            aria-label="Clear search"
            onClick={onClear}
            data-testid="search-clear-button"
            style={{ cursor: 'pointer', display: 'flex', background: 'none', border: 'none', padding: 0 }}
          >
            <X size={iconSize} style={{ flexShrink: 0 }} />
          </button>
        </InputElement>
      )}
    </Group>
  );
});
